import { nanoid } from "nanoid"
import {
  useCreateOpexCategory,
  useDeleteOpexCategory,
  useGetOpexSelectedCategories,
  useUpdateOpexSelectedCategory,
} from "@/feature/financial-model/ecommerce/opex/api"
import {
  OpexGranularity,
  OpexModelingApproach,
} from "@/feature/financial-model/ecommerce/opex/api/types.ts"
import {
  OPEX_GRANULARITY_LEVELS,
  OPEX_MODELLING_APPROACH,
} from "@/feature/financial-model/ecommerce/opex/constants.ts"

type Props = {
  financialModelId: string
  selectedCategoryId: string
}

export const useOpexSelectedCategory = ({
  selectedCategoryId,
  financialModelId,
}: Props) => {
  const { data: opexSelectedCategories } = useGetOpexSelectedCategories({
    financialModelId,
  })
  const selectedCategory = opexSelectedCategories.find(
    (c) => c.id === selectedCategoryId
  )
  if (!selectedCategory) {
    throw new Error(
      `SelectedCategoryContent > Selected category ${selectedCategoryId} not found`
    )
  }

  const { mutate: deleteOpexCategory } = useDeleteOpexCategory()

  const { mutate: updateCategory } = useUpdateOpexSelectedCategory()

  const { mutate: createOpexCategory } = useCreateOpexCategory()

  const onGranularityChange = (value: OpexGranularity) => {
    updateCategory({
      financialModelId,
      opex_category_id: selectedCategory.opex_category.id,
      is_selected: true,
      name: selectedCategory.opex_category.name,
      granularity_level: value,
      modeling_approach: selectedCategory.modeling_approach,
    })
  }

  const onModelingApproachChange = (value: OpexModelingApproach) => {
    updateCategory({
      financialModelId,
      opex_category_id: selectedCategory.opex_category.id,
      is_selected: true,
      name: selectedCategory.opex_category.name,
      granularity_level: selectedCategory.granularity_level,
      modeling_approach: value,
    })
  }

  const onAddSubCostCategory = (name: string = ``) => {
    const uniqueName = nanoid(4)
    createOpexCategory({
      financialModelId,
      parent_id: selectedCategory.opex_category.id,
      name: name || uniqueName,
      modeling_approach: OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE,
      granularity_level: OPEX_GRANULARITY_LEVELS.AGGREGATE_EXPENSES,
    })
  }

  const onDeleteSubCostCategory = () => {
    deleteOpexCategory({
      financialModelId,
      categoryId: selectedCategory.opex_category.id,
    })
  }
  return {
    data: selectedCategory,
    onGranularityChange,
    onModelingApproachChange,
    onAddSubCostCategory,
    onDeleteSubCostCategory,
  }
}
